'use client';

import type {
  AiApplicationComponentsResponseDto,
  AiComponentDto,
} from '@terra/shared-contracts-ai-applications';
import { Button, IconButton, useThemeMode } from '@terra/ui-design-system';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { Add01Icon, Remove01Icon } from '@terra/ui-icons';
import {
  Background,
  BackgroundVariant,
  ReactFlow,
  ReactFlowProvider,
  useEdgesState,
  useNodesState,
  useReactFlow,
  type ColorMode,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  COMPOSITION_CARD_HEIGHT,
  COMPOSITION_CARD_WIDTH,
  compositionLayoutEndpoints,
  compositionRelationLabel,
  layoutCompositionGraph,
  resolveCompositionCardDescription,
  resolveCompositionCardVisibility,
  resolveCompositionRoleText,
  resolveCompositionVisualKind,
} from '../utils/compositionGraphModel';
import { COMPOSITION_CANVAS_DOT } from '../utils/compositionGraphVisual';
import {
  CompositionEdge,
  type CompositionFlowEdge,
} from './AiApplicationCompositionEdge';
import { AiApplicationCompositionDetail } from './AiApplicationCompositionDetail';
import { AiApplicationCompositionLegend } from './AiApplicationCompositionLegend';
import {
  CompositionNodeView,
  type CompositionFlowNode,
} from './AiApplicationCompositionNode';

type AiApplicationCompositionGraphProps = {
  components: AiComponentDto[];
  relations: AiApplicationComponentsResponseDto['relations'];
  height?: number | string;
  minHeight?: number | string;
  persistentDetail?: boolean;
  onSelectComponent?: (componentId: string) => void;
};

const nodeTypes = { compositionNode: CompositionNodeView };
const edgeTypes = { compositionEdge: CompositionEdge };

export function AiApplicationCompositionGraph({
  components,
  relations,
  height = '515px',
  minHeight,
  persistentDetail,
  onSelectComponent,
}: AiApplicationCompositionGraphProps) {
  const layout = useMemo(
    () => layoutCompositionGraph({ components, relations }),
    [components, relations],
  );
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const activeId =
    selectedId && components.some((component) => component.id === selectedId)
      ? selectedId
      : layout.rootId;
  const selectedComponent =
    components.find((component) => component.id === activeId) ?? null;
  const handleSelect = useCallback(
    (componentId: string) => {
      setSelectedId(componentId);
      onSelectComponent?.(componentId);
    },
    [onSelectComponent],
  );

  return (
    <HStack
      h={height}
      w="full"
      minH={minHeight}
      alignItems="stretch"
      gap={0}
      overflow="hidden"
      bg="bg.canvas"
    >
      <VStack flex={1} minW={0} minH={0} gap={0} alignItems="stretch">
        <Box flex={1} minH={0} position="relative">
          <ReactFlowProvider>
            <CompositionCanvas
              components={components}
              relations={relations}
              activeId={activeId}
              onSelectComponent={handleSelect}
            />
          </ReactFlowProvider>
        </Box>
        <AiApplicationCompositionLegend />
      </VStack>
      {persistentDetail && selectedComponent ? (
        <AiApplicationCompositionDetail
          component={selectedComponent}
          components={components}
          relations={relations}
          onSelectComponent={handleSelect}
        />
      ) : null}
    </HStack>
  );
}

function CompositionCanvas({
  components,
  relations,
  activeId,
  onSelectComponent,
}: {
  components: AiComponentDto[];
  relations: AiApplicationComponentsResponseDto['relations'];
  activeId: string | null;
  onSelectComponent: (componentId: string) => void;
}) {
  const t = useTranslations('aiApplications.graph');
  const { mode: themeMode } = useThemeMode();
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState<CompositionFlowNode>(
    [],
  );
  const [edges, setEdges, onEdgesChange] = useEdgesState<CompositionFlowEdge>(
    [],
  );
  const layoutKey = components
    .map((component) => component.id)
    .concat(relations.map((relation) => relation.id))
    .join('|');
  const layoutKeyRef = useRef<string | null>(null);

  useEffect(() => {
    const preservePositions = layoutKeyRef.current === layoutKey;

    layoutKeyRef.current = layoutKey;
    const { positions } = layoutCompositionGraph({ components, relations });
    const componentsById = new Map(
      components.map((component) => [component.id, component]),
    );

    setNodes((current) => {
      const currentById = new Map(current.map((node) => [node.id, node]));

      return components.map((component) => {
        const existing = currentById.get(component.id);
        const laidOut = positions.get(component.id) ?? { x: 0, y: 0 };

        return {
          id: component.id,
          type: 'compositionNode' as const,
          position: preservePositions && existing ? existing.position : laidOut,
          width: COMPOSITION_CARD_WIDTH,
          height: COMPOSITION_CARD_HEIGHT,
          data: {
            component,
            visualKind: resolveCompositionVisualKind(component),
            roleText: resolveCompositionRoleText(component),
            description: resolveCompositionCardDescription(component),
            visibility: resolveCompositionCardVisibility(component),
            verified: component.verificationStatus === 'RUNTIME_VERIFIED',
            selected: component.id === activeId,
          },
        };
      });
    });
    setEdges(
      relations
        .filter(
          (relation) =>
            componentsById.has(relation.sourceId) &&
            componentsById.has(relation.targetId),
        )
        .map((relation) => {
          const { parentId, childId } = compositionLayoutEndpoints({
            relation,
            sourceType: componentsById.get(relation.sourceId)?.type,
          });

          return {
            id: relation.id,
            source: parentId,
            target: childId,
            label: compositionRelationLabel(relation),
            type: 'compositionEdge' as const,
            data: {
              hot: parentId === activeId || childId === activeId,
            },
          };
        }),
    );
  }, [activeId, components, layoutKey, relations, setEdges, setNodes]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      fitView({ padding: 0.12, duration: 0 });
    });

    return () => cancelAnimationFrame(frame);
  }, [fitView, layoutKey]);

  const handleNodeClick = useCallback<NodeMouseHandler<CompositionFlowNode>>(
    (_event, node) => {
      onSelectComponent(node.id);
    },
    [onSelectComponent],
  );

  return (
    <>
      <ReactFlow
        proOptions={{ hideAttribution: true }}
        colorMode={themeMode as ColorMode}
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={handleNodeClick}
        minZoom={0.25}
        maxZoom={1.6}
        nodesConnectable={false}
        fitView
        style={{ background: token('colors.bg.canvas') }}
      >
        <Background
          variant={BackgroundVariant.Dots}
          gap={16}
          size={1}
          color={COMPOSITION_CANVAS_DOT}
        />
      </ReactFlow>
      <VStack
        position="absolute"
        left="16px"
        bottom="16px"
        gap={0}
        bg="bg.default"
        borderWidth="1px"
        borderColor="border.neutral.subtle"
        borderRadius="6px"
        overflow="hidden"
        zIndex={1}
      >
        <IconButton
          aria-label={t('zoomIn')}
          variant="ghost"
          size="xs"
          borderRadius={0}
          borderBottomWidth="1px"
          borderColor="border.neutral.subtle"
          onClick={() => zoomIn({ duration: 150 })}
        >
          <Add01Icon size={16} />
        </IconButton>
        <IconButton
          aria-label={t('zoomOut')}
          variant="ghost"
          size="xs"
          borderRadius={0}
          borderBottomWidth="1px"
          borderColor="border.neutral.subtle"
          onClick={() => zoomOut({ duration: 150 })}
        >
          <Remove01Icon size={16} />
        </IconButton>
        <Button
          aria-label={t('zoomFit')}
          variant="ghost"
          size="xs"
          borderRadius={0}
          h="30px"
          minW="30px"
          px={1}
          onClick={() => fitView({ padding: 0.12, duration: 150 })}
        >
          {t('zoomFit')}
        </Button>
      </VStack>
    </>
  );
}
